import React from "react";
import { useTranslation } from "react-i18next";
import { Check, X, HelpCircle } from "lucide-react";
import type { VoteType } from "../../lib/insights/types";
import type { Participant } from "./ResultsTable";

interface SlotResponsesListProps {
  participants: Participant[];
  slotId: string;
}

const SlotResponsesList: React.FC<SlotResponsesListProps> = ({
  participants,
  slotId,
}) => {
  const { t } = useTranslation("polls");

  const groupedVotes = React.useMemo(() => {
    const groups: Record<VoteType | "empty", Participant[]> = {
      yes: [],
      maybe: [],
      no: [],
      empty: [],
    };

    for (const p of participants) {
      const vote: VoteType | undefined = p.votes[slotId];
      const key = vote ?? "empty";
      groups[key].push(p);
    }

    return groups;
  }, [participants, slotId]);

  const getIcon = (vote: VoteType | "empty") => {
    switch (vote) {
      case "yes":
        return (
          <Check
            className="w-3.5 h-3.5 text-vote-available-foreground"
            aria-hidden
          />
        );
      case "no":
        return (
          <X
            className="w-3.5 h-3.5 text-vote-unavailable-foreground"
            aria-hidden
          />
        );
      case "maybe":
        return (
          <HelpCircle
            className="w-3.5 h-3.5 text-vote-maybe-foreground"
            aria-hidden
          />
        );
      case "empty":
        return <div className="w-3.5 h-3.5" aria-hidden />;
    }
  };

  const getLabel = (vote: VoteType | "empty") => {
    switch (vote) {
      case "yes":
        return t("available");
      case "no":
        return t("unavailable");
      case "maybe":
        return t("maybe");
      case "empty":
        return t("no_response");
    }
  };

  const getColor = (vote: VoteType | "empty") => {
    switch (vote) {
      case "yes":
        return "text-vote-available-foreground";
      case "no":
        return "text-vote-unavailable-foreground";
      case "maybe":
        return "text-vote-maybe-foreground";
      case "empty":
        return "text-muted-foreground";
    }
  };

  return (
    <div
      data-testid={`slot-responses-list-${slotId}`}
      className="mt-3 space-y-2 text-sm"
    >
      {(["yes", "maybe", "no", "empty"] as const).map((vote) => {
        const group = groupedVotes[vote];
        if (group.length === 0) return null;

        return (
          <div key={vote} className="flex items-start gap-2">
            <div className="flex shrink-0 items-center gap-1.5 mt-0.5">
              {getIcon(vote)}
              <span className={`text-xs font-medium ${getColor(vote)}`}>
                {getLabel(vote)}
              </span>
            </div>
            <div className="flex min-w-0 flex-wrap gap-1">
              {group.map((p) => (
                <span
                  key={p.id}
                  data-testid={`response-name-${slotId}-${p.id}`}
                  className="max-w-full break-words text-xs bg-muted/50 px-2 py-1 rounded"
                >
                  {p.name}
                  {p.isYou && " (vous)"}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SlotResponsesList;
